import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import CallToAction from '../components/CallToAction';

const FeatureCard = ({ title, items }) => {
  return (
    <div className="glass-card p-8 mb-8 transition-all duration-300 hover:shadow-lg">
      <h3 className="text-2xl font-bold mb-4">{title}</h3>
      <ul className="space-y-3">
        {items.map((item, index) => (
          <li key={index} className="flex items-start">
            <span className="text-primary mr-2 mt-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ProblemItem = ({ text }) => {
  return (
    <div className="flex items-start mb-4">
      <span className="text-red-500 mr-2 mt-1">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      </span>
      <span className="text-lg">{text}</span>
    </div>
  );
};

const BenefitItem = ({ text }) => {
  return (
    <div className="flex items-start mb-4">
      <span className="text-green-500 mr-2 mt-1">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      </span>
      <span className="text-lg">{text}</span>
    </div>
  );
};

const StepItem = ({ number, title, description }) => {
  return (
    <div className="flex mb-8">
      <div className="flex-shrink-0 mr-6">
        <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold">
          {number}
        </div>
      </div>
      <div>
        <h4 className="text-xl font-bold mb-2">{title}</h4>
        <p className="text-gray-700">{description}</p>
      </div>
    </div>
  );
};

const TestimonialCard = ({ quote, author, business }) => {
  return (
    <div className="glass-card p-8 mb-8 transition-all duration-300 hover:shadow-lg">
      <div className="text-xl italic mb-4">"{quote}"</div>
      <div className="font-bold">{author}</div>
      <div className="text-gray-700">{business}</div>
    </div>
  );
};

const CrmPage = () => {
  useEffect(() => {
    document.title = "Our CRM System - Turn More Leads Into Paying Customers";
  }, []);

  return (
    <>
      <PageHeader 
        title="The CRM That Actually Makes You Money" 
        subtitle="Stop losing leads to poor follow-up. Our white-labeled CRM system helps contractors convert more prospects into paying customers - automatically."
      />
      
      {/* Hero Section with CTA */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <Link to="/contact#booking-calendar" className="btn btn-primary btn-lg">
              See How It Works
            </Link>
          </div>
          
          {/* Mock-up image */}
          <div className="max-w-5xl mx-auto rounded-lg shadow-xl overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
              alt="CRM Dashboard" 
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>
      
      {/* Problem/Solution Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Why Most Contractors Lose 70% of Their Leads
            </h2>
            
            <div className="glass-card p-8 mb-8">
              <ProblemItem text="Missed follow-ups because you're too busy on job sites" />
              <ProblemItem text="No system to track where leads came from or their status" />
              <ProblemItem text="Poor communication that makes you look unprofessional" />
              <ProblemItem text="Manual processes that waste hours every week" />
              <ProblemItem text="Customers who don't leave reviews (or leave bad ones)" />
              
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-xl font-semibold">
                  Our CRM eliminates these problems so you can focus on what you do best - the actual work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Everything You Need to Scale Your Business
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <FeatureCard 
              title="Smart Lead Management"
              items={[
                "Automatically capture leads from your website, calls, and emails",
                "Track every conversation and follow-up",
                "Never miss a potential customer again",
                "See exactly which marketing efforts bring in the most money"
              ]}
            />
            
            <FeatureCard 
              title="Automated Follow-Up System"
              items={[
                "Send professional follow-up messages automatically",
                "Nurture leads until they're ready to buy",
                "Schedule appointments without the back-and-forth",
                "Convert more estimates into actual jobs"
              ]}
            />
            
            <FeatureCard 
              title="Review Management That Works"
              items={[
                "Automatically request reviews from happy customers",
                "Get notified of new reviews (good or bad)",
                "Respond professionally to build your reputation",
                "Turn 5-star reviews into more leads"
              ]}
            />
            
            <FeatureCard 
              title="AI-Powered Business Growth"
              items={[
                "Smart automation that learns your business",
                "AI chatbot to qualify leads 24/7",
                "Automated appointment scheduling",
                "Intelligent follow-up sequences that convert"
              ]}
            />
          </div>
        </div>
      </section>
      
      {/* Results Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              What This Means for Your Bottom Line
            </h2>
            
            <div className="glass-card p-8">
              <BenefitItem text="Convert 40-60% more leads into paying customers" />
              <BenefitItem text="Save 10+ hours per week on administrative tasks" />
              <BenefitItem text="Get 3x more positive reviews automatically" />
              <BenefitItem text="Never lose another lead to poor follow-up" />
              <BenefitItem text="Look more professional than your competition" />
            </div>
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Simple Setup, Powerful Results
            </h2>
            
            <div className="px-4">
              <StepItem 
                number="1"
                title="Instant Setup"
                description="We configure everything with your branding and business information so you're ready to go from day one."
              />
              
              <StepItem 
                number="2"
                title="Lead Capture"
                description="All inquiries from your website, phone calls, and emails flow into one organized system you can access anywhere."
              />
              
              <StepItem 
                number="3"
                title="Automated Follow-Up"
                description="Professional follow-up sequences run on autopilot, nurturing leads and keeping you top-of-mind."
              />
              
              <StepItem 
                number="4"
                title="Convert & Close"
                description="Watch more leads become paying customers as your business runs more efficiently and professionally."
              />
            </div>
            
            <div className="text-center mt-8">
              <Link to="/contact#booking-calendar" className="btn btn-primary">
                See It in Action
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Pricing Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Included FREE with Every Website
            </h2>
            
            <div className="glass-card p-8 border-2 border-primary">
              <div className="text-center mb-8">
                <div className="text-3xl font-bold mb-2">6 Months Completely FREE</div>
                <div className="text-xl text-gray-700">$300 Value</div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center p-4">
                  <div className="font-bold mb-2">Full Setup & Training</div>
                  <div className="text-gray-700">$200 Value</div>
                </div>
                
                <div className="text-center p-4">
                  <div className="font-bold mb-2">Custom Branding</div>
                  <div className="text-gray-700">$100 Value</div>
                </div>
                
                <div className="text-center p-4">
                  <div className="font-bold mb-2">Unlimited Support</div>
                  <div className="text-gray-700">Included</div>
                </div>
              </div>
              
              <div className="text-center p-4 border-t border-gray-200 pt-6">
                <div className="font-bold text-xl mb-2">After 6 Months</div>
                <div className="text-3xl font-bold text-primary mb-2">$100/month</div>
                <div className="text-gray-700 mb-4">Includes website hosting + CRM system</div>
                <div className="text-sm">No contracts - cancel anytime if it's not making you money</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Social Proof Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Real Results from Real Contractors
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <TestimonialCard 
              quote="Increased my close rate from 30% to 65% in just 2 months"
              author="Mike Johnson"
              business="Johnson Plumbing"
            />
            
            <TestimonialCard 
              quote="I get 5x more Google reviews now - all automatically"
              author="Sarah Williams"
              business="Williams Electrical"
            />
            
            <TestimonialCard 
              quote="Saves me 15 hours a week and makes me look way more professional"
              author="David Miller"
              business="Miller Roofing"
            />
          </div>
        </div>
      </section>
      
      {/* Final CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Stop Losing Leads?
            </h2>
            <p className="text-xl mb-8">
              Get your website + our complete CRM system. See how much more money you can make with proper follow-up.
            </p>
            
            <div className="flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-4">
              <Link to="/contact#booking-calendar" className="btn btn-light btn-lg">
                Get My Website + CRM Free Trial
              </Link>
              <Link to="/contact#booking-calendar" className="btn btn-outline-light btn-lg">
                Book a Quick Demo Call
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      <CallToAction title="Ready to convert more leads into paying customers?" />
    </>
  );
};

export default CrmPage;
